import { KanbanBoard, SprintTask } from '../entities/sprint/model/types';

/**
 * Gap-based position for a task sitting at `index` in `tasks` (which already
 * includes it). Only the moved task's position is recomputed - neighbors
 * keep whatever position they already had, so a single move never has to
 * touch every row in the column.
 */
export function computePositionAtIndex(
  tasks: Array<{ position?: number }>,
  index: number,
): number {
  const prev = tasks[index - 1]?.position;
  const next = tasks[index + 1]?.position;

  if (prev != null && next != null) return Math.round((prev + next) / 2);
  if (prev != null) return prev + 1000;
  if (next != null) return next - 1000;
  return 1000;
}

/**
 * Reconciles the board to a task's authoritative server-side state -
 * removing it from wherever it currently sits and reinserting it into the
 * column matching `currentTask.status` at the position implied by
 * `currentTask.position`. Used both to resolve a 409 (server's `currentTask`)
 * and to sync a successful move's response (bumped `version`).
 */
export function reconcileBoardWithServerTask(
  board: KanbanBoard,
  currentTask: { id: string; status: string; position: number; version: number },
): KanbanBoard {
  let removedTask: SprintTask | undefined;

  const columnsWithoutTask = board.columns.map((col) => {
    const idx = col.tasks.findIndex((t) => t.id === currentTask.id);
    if (idx === -1) return col;
    removedTask = col.tasks[idx];
    return { ...col, tasks: col.tasks.filter((t) => t.id !== currentTask.id) };
  });

  if (!removedTask) {
    return { ...board, columns: columnsWithoutTask };
  }

  const targetColumnIndex = columnsWithoutTask.findIndex(
    (col) => col.status === currentTask.status,
  );
  if (targetColumnIndex === -1) {
    return { ...board, columns: columnsWithoutTask };
  }

  const mergedTask: SprintTask = {
    ...removedTask,
    status: currentTask.status,
    position: currentTask.position,
    version: currentTask.version,
  };

  const targetColumn = columnsWithoutTask[targetColumnIndex];
  const insertAt = targetColumn.tasks.findIndex(
    (t) => (t.position ?? 0) > currentTask.position,
  );
  const insertIndex = insertAt === -1 ? targetColumn.tasks.length : insertAt;

  const newTasks = [
    ...targetColumn.tasks.slice(0, insertIndex),
    mergedTask,
    ...targetColumn.tasks.slice(insertIndex),
  ];

  const newColumns = columnsWithoutTask.map((col, i) =>
    i === targetColumnIndex ? { ...col, tasks: newTasks } : col,
  );

  return { ...board, columns: newColumns };
}
